import React, { useState, useEffect } from 'react';
import { RemoteAlertGroup, AlertRecipient } from '../../types/remoteAlerts';
import { Sensor } from '../../types';
import { User } from '../../types/user';
import { useGetZonesQuery } from '../../services/zoneApi';
import { useGetZoneSensorsQuery } from '../../services/sensorApi';
import { useGetUsersQuery } from '../../services/userApi';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store/store';

interface RemoteAlertGroupDialogProps {
  onClose: () => void;
  onSave: (group: { name: string; sensorIds: number[]; recipients: AlertRecipient[] }) => void;
  group?: RemoteAlertGroup;
}

const RemoteAlertGroupDialog: React.FC<RemoteAlertGroupDialogProps> = ({ onClose, onSave, group }) => {
  const [name, setName] = useState('');
  const [sensorIds, setSensorIds] = useState<number[]>([]);
  const [recipients, setRecipients] = useState<AlertRecipient[]>([]);
  
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const { data: zones = [] } = useGetZonesQuery();
  const { data: zoneSensors = [], isLoading: sensorsLoading } = useGetZoneSensorsQuery(selectedZoneId!, { skip: !selectedZoneId });
  const { data: users = [], isLoading: usersLoading } = useGetUsersQuery();
  const user = useSelector((state: RootState) => state.auth.user);

  // Auto-select the first zone when editing a group and no zone is selected
  useEffect(() => {
    if (group && group.sensorIds && group.sensorIds.length > 0 && zones.length > 0 && !selectedZoneId) {
      setSelectedZoneId(zones[0].zone_id);
    }
  }, [group, zones, selectedZoneId]);

  // Set recipients and name only when group or users change
  useEffect(() => {
    if (group && users.length > 0) {
      setName(group.name);
      const mappedRecipients = group.recipients.map(r => ({
        ...r,
        userId: String(r.userId)
      }));
      setRecipients(mappedRecipients);
    }
  }, [group, users]);

  // Set sensorIds only when selectedZoneId or zoneSensors change
  useEffect(() => {
    if (group && selectedZoneId && zoneSensors.length > 0) {
      const groupSensorIds = group.sensorIds || [];
      const zoneSensorIds = zoneSensors.map(s => s.sensor_id);
      const preselected = groupSensorIds.filter(id => zoneSensorIds.includes(id));
      setSensorIds(preselected);
    } else if (group) {
      setSensorIds([]);
    }
  }, [group, selectedZoneId, zoneSensors]);

  const handleSave = async () => {
    const payload = { name, sensors: sensorIds, recipients };
    const localGroup = { name, sensorIds, recipients };
    try {
      const response = await fetch('https://n8n.ts1cloud.com/webhook/2400133a-6599-4199-83f0-1bf646eaaa16', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.companyID && { 'x-company-id': user.companyID.toString() }),
          ...(user?.companyName && { 'x-company-name': user.companyName }),
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error('Failed to send alert group');
      }
      onSave(localGroup);
    } catch (error) {
      alert('Failed to save alert group: ' + (error as Error).message);
    }
  };
  
  const handleSensorToggle = (sensorId: number) => {
    setSensorIds(prev =>
      prev.includes(sensorId) ? prev.filter(id => id !== sensorId) : [...prev, sensorId]
    );
  };
  
  const addRecipient = () => {
    if (users.length > 0) {
      setRecipients(prev => [...prev, { userId: String(users[0].id), email: true, sms: false, push: false }]);
    }
  };
  
  const handleRecipientChange = (index: number, field: keyof AlertRecipient, value: any) => {
    const newRecipients = [...recipients];
    (newRecipients[index] as any)[field] = value;
    setRecipients(newRecipients);
  };

  const removeRecipient = (index: number) => {
    setRecipients(prev => prev.filter((_, i) => i !== index));
  };

  // Use real sensors from API for the selected zone
  const filteredSensors = zoneSensors.map(s => ({
    id: s.sensor_id,
    name: s.sensor_name,
    serialNo: s.sensor_serialNo,
  }));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-2xl">
        <h2 className="text-2xl font-bold mb-4 text-white">{group ? 'Edit' : 'Add'} Alert Group</h2>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="groupName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Group Name</label>
            <input
              type="text"
              id="groupName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="zoneSelect" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Zone</label>
            <select
              id="zoneSelect"
              value={selectedZoneId ?? ''}
              onChange={e => setSelectedZoneId(e.target.value ? Number(e.target.value) : null)}
              className="block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm mb-4"
            >
              <option value="">Select a zone...</option>
              {zones.map(zone => (
                <option key={zone.zone_id} value={zone.zone_id}>{zone.zone_name}</option>
              ))}
            </select>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Sensors</h3>
            <div className="mt-2 grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
              {sensorsLoading ? (
                <span className="text-gray-400 col-span-3">Loading sensors...</span>
              ) : filteredSensors.length > 0 ? (
                filteredSensors.map(sensor => (
                  <label key={sensor.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={sensorIds.includes(sensor.id)}
                      onChange={() => handleSensorToggle(sensor.id)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{sensor.name}</span>
                  </label>
                ))
              ) : (
                <span className="text-gray-400 col-span-3">No sensors in this zone.</span>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Recipients</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {recipients.map((recipient, index) => (
                <div key={index} className="flex items-center space-x-2 p-2 border rounded-md dark:border-gray-600">
                  <select
                    value={recipient.userId}
                    onChange={(e) => handleRecipientChange(index, 'userId', e.target.value)}
                    className="block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm"
                  >
                    {usersLoading ? (
                      <option>Loading users...</option>
                    ) : users.length > 0 ? (
                      users.map(user => (
                        <option key={user.id} value={String(user.id)}>{user.firstName} {user.lastName}</option>
                      ))
                    ) : (
                      <option>No users found</option>
                    )}
                  </select>
                  <label className="flex items-center"><input type="checkbox" checked={recipient.email} onChange={(e) => handleRecipientChange(index, 'email', e.target.checked)} className="mr-1" /> Email</label>
                  <label className="flex items-center"><input type="checkbox" checked={recipient.sms} onChange={(e) => handleRecipientChange(index, 'sms', e.target.checked)} className="mr-1" /> SMS</label>
                  <label className="flex items-center"><input type="checkbox" checked={recipient.push} onChange={(e) => handleRecipientChange(index, 'push', e.target.checked)} className="mr-1" /> Push</label>
                  <button onClick={() => removeRecipient(index)} className="text-red-500 hover:text-red-700">&times;</button>
                </div>
              ))}
            </div>
            <button onClick={addRecipient} className="mt-2 text-sm text-blue-600 hover:underline">Add Recipient</button>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">Cancel</button>
          <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Save</button>
        </div>
      </div>
    </div>
  );
};

export default RemoteAlertGroupDialog; 